// Board layout — docs/05-board-scene.md §3.2. Metres; Y-up; +Z = front (the player's desk).
import type { LocationId } from '@bcm/rules';

export interface RoomLayout {
  id: LocationId;
  x: [number, number];
  z: [number, number];
  door: { x: number; z: number; side: 'n' | 's' | 'e' | 'w' };
  stations: [[number, number], [number, number]];
  /** Floor tint and accent (hero prop / glow). */
  tint: string;
  accent: string;
  prop: 'circle' | 'flasks' | 'column' | 'shelves' | 'orb' | 'typewriters' | 'counter' | 'basin' | 'shop' | 'desk' | 'dais' | 'reception' | 'annex';
  /** An open area of a hall (no walls of its own), e.g. the Office of Precedence in the entrance hall. */
  open?: boolean;
}

export const room = (
  id: LocationId,
  x: [number, number],
  z: [number, number],
  door: RoomLayout['door'],
  tint: string,
  accent: string,
  prop: RoomLayout['prop'],
): RoomLayout => {
  const cx = (x[0] + x[1]) / 2;
  const d = z[1] - z[0];
  const sz = (z[0] + z[1]) / 2 + 0.22 * d;
  const half = Math.min(1.5, Math.max(0.6, 0.12 * (x[1] - x[0])));
  return { id, x, z, door, stations: [[cx - half, sz], [cx + half, sz]], tint, accent, prop };
};

// Layout v3 (2026-09-25): a real circulation plan. A 2.4 m north hall serves the back row, 2.5 m
// side corridors run the length of the building, and a front hall behind the main entrance joins
// them, so every door opens onto a hall. The Planning Office now opens onto the front hall.
//
//   z −13 … −4     back row: Conjuration · Transmutation · Evocation · Archive (doors south)
//   z −4 … −1.6    north hall
//   z −1.6 … 13.8  Arcanium | west corridor | centre band + Planning Office | east corridor | Auditor
//   z 13.8 … 20.6  entrance hall: the lobby where idle Assistants wait (main entrance at x 0),
//                  with the Office of Precedence (P-11) at its east end
//
// 2026-09-26: the building front moved out to z 20.6, so the benches stand inside the entrance hall
// instead of on a terrace between two parallel walls.
export const HALL = { north: [-4, -1.6] as const, front: [13.8, 20.6] as const, west: [-12.5, -10] as const, east: [12.5, 15] as const };
export const BUILDING_X = [-24.5, 24.5] as const;
export const BUILDING_Z = [-13, 20.6] as const;
/** The main entrance in the building front. */
export const ENTRANCE_X = 0;

export const ROOMS: RoomLayout[] = [
  room('conjuration', [-24.5, -8.5], [-13, -4], { x: -16.5, z: -4, side: 's' }, '#8a4a40', '#e0503f', 'circle'),
  room('transmutation', [-8.5, 1.75], [-13, -4], { x: -3.4, z: -4, side: 's' }, '#8a7440', '#f0c24a', 'flasks'),
  room('evocation', [1.75, 12.25], [-13, -4], { x: 7, z: -4, side: 's' }, '#43587e', '#5a9cff', 'column'),
  room('archive', [12.25, 24.5], [-13, -4], { x: 18.4, z: -4, side: 's' }, '#4d6b4c', '#6fd08a', 'shelves'),
  room('arcanium', [-24.5, -12.5], [-1.6, 13.8], { x: -12.5, z: 6, side: 'e' }, '#7a6048', '#e8b06a', 'shop'),
  room('enchantment', [-10, 1.5], [-1.6, 3.6], { x: -4.25, z: -1.6, side: 'n' }, '#3f6b69', '#4fd2c6', 'typewriters'),
  room('procuration', [-10, 1.5], [3.6, 8.8], { x: -10, z: 6.2, side: 'w' }, '#7a6a52', '#d9c08e', 'counter'),
  room('divination', [1.5, 12.5], [-1.6, 3.6], { x: 7, z: -1.6, side: 'n' }, '#3f6452', '#7fe0b0', 'orb'),
  room('mana-conduit', [1.5, 12.5], [3.6, 8.8], { x: 12.5, z: 6.2, side: 'e' }, '#6e3c5c', '#f26bbd', 'basin'),
  room('grand-auditor', [15, 24.5], [-1.6, 13.8], { x: 15, z: 6, side: 'w' }, '#8a7a66', '#e9d49a', 'dais'),
  room('planning-office', [-10, 12.5], [8.8, 13.8], { x: 1.25, z: 13.8, side: 's' }, '#6d5a47', '#f0d7a8', 'desk'),
  // P-11: a desk in the east end of the entrance hall, behind a brass rail open to the west.
  {
    ...room('precedence', [17.2, 24.5], [13.8, 20.6], { x: 17.2, z: 18.2, side: 'w' }, '#6a2c32', '#e8c774', 'reception'),
    // One station only (capacity 1): in front of the desk. The second entry is never used.
    stations: [[21.0, 18.2], [21.0, 18.2]],
    open: true,
  },
];

export const ROOM_BY_ID = new Map(ROOMS.map((r) => [r.id, r] as const));

// ─── Annex bays (P-18) ───────────────────────────────────────────────────────
// Two wings at the front corners of the model, beside the spell display (west) and the City
// Problems (east): table space the camera already frames, so opening a room never shrinks the
// board. Each wing is a small plinth of its own against the main one; an open Annex room stands on it
// with its door facing the Ministry.

export interface AnnexBayRect {
  /** The wing's plinth. */
  x: [number, number];
  z: [number, number];
  /** Which way the Ministry is: −1 west, +1 east (the room's door faces it). */
  toward: -1 | 1;
}

export const ANNEX_BAYS: AnnexBayRect[] = [
  { x: [28.9, 38.9], z: [13.2, 23.6], toward: -1 }, // bay I: front right, by the City Problems
  { x: [-38.9, -28.9], z: [13.2, 23.6], toward: 1 }, // bay II: front left, by the spell display
];

/** The room rect inside a bay: a margin of lawn all round, a little more at the back. */
export function annexRoomRect(bay: number): { x: [number, number]; z: [number, number] } {
  const b = ANNEX_BAYS[bay]!;
  return { x: [b.x[0] + 0.9, b.x[1] - 0.9], z: [b.z[0] + 1.2, b.z[1] - 0.9] };
}

/** An Annex room's layout in a bay: stations in the front third, the door toward the Ministry. */
export function annexLayout(id: LocationId, bay: number, tint: string, accent: string): RoomLayout {
  const r = annexRoomRect(bay);
  const b = ANNEX_BAYS[bay]!;
  const cz = (r.z[0] + r.z[1]) / 2;
  const doorX = b.toward < 0 ? r.x[0] : r.x[1];
  return room(id, r.x, r.z, { x: doorX, z: cz, side: b.toward < 0 ? 'w' : 'e' }, tint, accent, 'annex');
}

/** The seat line of the lobby pews in the entrance hall. */
export const LOBBY_Z = 18.1;
// Benches fill outward from the entrance (which stays clear), alternating sides. Each seat's corner
// (standard, pew, Stamp of Precedence on its rug) is 6.4 m wide: the outer ones stop short of the
// west wall and of the Office of Precedence's rail.
export const LOBBY_X = [-6.5, 6.5, -13.7, 13.7, -21];
/** The lobby pew: its length, and the top of its cushion, where waiting clerks stand. */
export const PEW_WIDTH = 4.0;
export const PEW_TOP = 0.56;
/** The seat standard (inner end) and the Stamp of Precedence (outer end) flank the pew this far out. */
export const BENCH_FLANK = 2.55;
/** Waiting clerks stand this far apart: their hat brims (about 1 m across) clear each other. */
const BENCH_STEP = 1.15;

/** Where the `slot`-th of `count` waiting clerks stands: centred on the pew, squeezed if it's full. */
export function benchSlot(seat: number, slot: number, count: number): [number, number] {
  const n = Math.max(1, count);
  const reach = PEW_WIDTH / 2 - 0.32; // the outer clerks keep their feet on the pew
  const step = n > 1 ? Math.min(BENCH_STEP, (2 * reach) / (n - 1)) : 0;
  return [(LOBBY_X[seat] ?? 0) + (slot - (n - 1) / 2) * step, LOBBY_Z];
}

/** Which way a bench's outer end points: −1 on the west side of the entrance, +1 on the east. */
export function benchSide(seat: number): number {
  return (LOBBY_X[seat] ?? 0) < ENTRANCE_X ? -1 : 1;
}

export function roomCenter(id: LocationId): [number, number] {
  const r = ROOM_BY_ID.get(id)!;
  return [(r.x[0] + r.x[1]) / 2, (r.z[0] + r.z[1]) / 2];
}
